/**
 * normalizeApiError
 *
 * Shared, pluggable error normalization for HTTP responses (axios,
 * fetch, or thrown `Error`s). The point of this module is to give the
 * UI ONE place to extract a user-friendly message + structured
 * metadata from the chaos of "could be axios, could be a thrown
 * Error, could be a JSON envelope from the backend".
 *
 * The previous implementation (kept as a deprecation shim at
 * `util/normalizeApiError.ts`) preferred `error.message` over the
 * backend response payload. For axios errors `error.message` is the
 * useless string `"Request failed with status code 400"`, so the user
 * was shown that instead of the backend's "Invalid username or
 * password". This module flips the order and adds:
 *
 *  - Stable error `code` extraction from common backend shapes
 *    (`code`, `error_code`, `errorCode`).
 *  - `requestId` extraction (`requestId`, `request_id`) for support
 *    correlation -- forwarded to Sonner's `description` slot via
 *    `notify.errorFromApi` so users can copy the id when reporting.
 *  - `fieldErrors` normalization for forms backed by per-field
 *    validation, regardless of whether the backend returns
 *    `errors`, `fieldErrors`, or `field_errors`.
 *  - Optional `codeMap` for domain-level localization: pass
 *    `{ INVALID_CREDENTIALS: t("auth.invalidCreds") }` and the
 *    matching backend code wins over the backend's English message.
 *
 * See `docs/architecture/forms-standard.md` section 4 for the
 * end-to-end contract.
 */

/**
 * Structured result from `normalizeApiError`. `message` is always
 * populated -- it is the user-facing fallback when no specific
 * resolution succeeds. The other fields are best-effort extractions
 * from the underlying error.
 */
export interface NormalizedApiError {
  /** Best user-facing message. Always defined; defaults to options.fallback. */
  message: string;
  /** Stable error code from backend (e.g., "INVALID_CREDENTIALS"). */
  code?: string;
  /** HTTP status code if the error came from the API client. */
  status?: number;
  /** Request id from backend response, useful for support correlation. */
  requestId?: string;
  /** Per-field validation errors keyed by field name. */
  fieldErrors?: Record<string, string>;
  /**
   * The original error preserved for logging/observability. Do NOT
   * render this directly to the user -- it is typically an `Error`
   * with a non-localized, technical message.
   */
  cause?: unknown;
}

export interface NormalizeApiErrorOptions {
  /**
   * User-facing fallback message used when none of the resolution
   * steps below produce a usable string. Should already be localized.
   */
  fallback: string;
  /**
   * Optional code -> localized message map. Lookups happen BEFORE
   * the backend's response message, so domains can override specific
   * known error codes with their own copy. Use this to localize
   * error messages whose backend payloads are still English.
   */
  codeMap?: Record<string, string>;
}

interface AxiosLikeShape {
  message?: unknown;
  response?: {
    status?: unknown;
    data?: unknown;
  };
}

const AXIOS_DEFAULT_MESSAGE_PATTERN = /^Request failed with status code \d+/i;
const NETWORK_ERROR_HINTS = ["Network Error", "ERR_NETWORK", "ECONNREFUSED", "ECONNABORTED", "timeout"];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const v of values) {
    if (typeof v === "string" && v.trim().length > 0) {
      return v.trim();
    }
  }
  return undefined;
};

/**
 * Pull per-field validation errors out of the response payload.
 * Accepts the common shapes:
 *   { errors: { email: "..." } }
 *   { errors: { email: ["...", "..."] } }   -> takes first
 *   { fieldErrors: { email: "..." } }
 *   { field_errors: { email: "..." } }
 */
const extractFieldErrors = (data: Record<string, unknown>): Record<string, string> | undefined => {
  const candidate =
    (isPlainObject(data.errors) && (data.errors as Record<string, unknown>)) ||
    (isPlainObject(data.fieldErrors) && (data.fieldErrors as Record<string, unknown>)) ||
    (isPlainObject(data.field_errors) && (data.field_errors as Record<string, unknown>)) ||
    null;
  if (!candidate) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [field, value] of Object.entries(candidate)) {
    if (typeof value === "string" && value.trim().length > 0) {
      out[field] = value.trim();
    } else if (Array.isArray(value)) {
      const first = value.find((v) => typeof v === "string" && v.trim().length > 0);
      if (typeof first === "string") {
        out[field] = first.trim();
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/**
 * Normalize any thrown / rejected value into a structured user-facing
 * error. The function never throws -- a non-object error returns the
 * fallback unchanged.
 *
 * Resolution order for `message`:
 *   1. `options.codeMap[code]` if a backend code is present and mapped.
 *   2. `error.response.data.message` (canonical backend message).
 *   3. `error.response.data.error` (alternate backend shape).
 *   4. `error.response.data.detail` (FastAPI-style shape).
 *   5. `error.message` ONLY if it is NOT axios's default
 *      `"Request failed with status code N"` (that string is purely
 *      technical and was the source of the original bug).
 *   6. `options.fallback`.
 */
export function normalizeApiError(error: unknown, options: NormalizeApiErrorOptions): NormalizedApiError {
  const result: NormalizedApiError = { message: options.fallback, cause: error };

  if (!isPlainObject(error)) {
    return result;
  }

  const errObj = error as AxiosLikeShape & Record<string, unknown>;
  const response = isPlainObject(errObj.response) ? errObj.response : undefined;
  const data = response && isPlainObject(response.data) ? response.data : undefined;

  if (response && typeof response.status === "number") {
    result.status = response.status;
  }

  if (data) {
    result.code = firstNonEmptyString(data.code, data.error_code, data.errorCode);
    result.requestId = firstNonEmptyString(data.requestId, data.request_id, data.correlationId, data.correlation_id);
    result.fieldErrors = extractFieldErrors(data);
  }

  if (result.code && options.codeMap && options.codeMap[result.code]) {
    result.message = options.codeMap[result.code];
    return result;
  }

  if (data) {
    const responseMessage = firstNonEmptyString(data.message, data.error, data.detail);
    if (responseMessage) {
      result.message = responseMessage;
      return result;
    }
  }

  // Only fall through to error.message if it is not the axios default
  // technical message -- otherwise users see "Request failed with
  // status code 400" which is the bug this module exists to fix.
  if (typeof errObj.message === "string" && errObj.message.trim().length > 0) {
    const trimmedMessage = errObj.message.trim();
    if (!AXIOS_DEFAULT_MESSAGE_PATTERN.test(trimmedMessage)) {
      result.message = trimmedMessage;
      return result;
    }
  }

  return result;
}

/**
 * Convenience wrapper for callers that only need the user-facing
 * string (the most common case in form `setError(...)` flows).
 *
 * Backwards compatible with the previous `normalizeApiError(error,
 * fallback)` signature exported from `util/normalizeApiError.ts`.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  codeMap?: Record<string, string>
): string {
  return normalizeApiError(error, { fallback, codeMap }).message;
}

/**
 * Heuristic check for transport-level failures (offline, DNS, etc.)
 * vs. application-level errors. Useful for showing "Check your
 * internet connection" rather than the generic fallback.
 */
export function isNetworkError(error: unknown): boolean {
  if (!isPlainObject(error)) {
    return false;
  }
  // Axios sets `code === "ERR_NETWORK"` and no `response` for
  // transport failures.
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && NETWORK_ERROR_HINTS.some((hint) => code.includes(hint))) {
    return true;
  }
  if (!(error as AxiosLikeShape).response && typeof (error as { message?: unknown }).message === "string") {
    const msg = (error as { message: string }).message;
    return NETWORK_ERROR_HINTS.some((hint) => msg.toLowerCase().includes(hint.toLowerCase()));
  }
  return false;
}
